/* eslint-disable react-hooks/exhaustive-deps */
import {
  ACCEPTED_FILE_TYPES_WITHOUT_AUDIO_VIDEO,
  IReferralInfo,
} from "@home-health-notify/domain";
import AddIcon from "@mui/icons-material/Add";
import { Box as ReferralFilesWrapper } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { useReferralFieldAutocomplete, useTranslation } from "hhn-commons";
import { useSnackbar } from "notistack";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { getDropZoneBaseConfig } from "../common/DropZoneConfig";
import {
  FileObjectType,
  getFileParams,
  mapFilesToFileObjectType,
} from "../uploader/FileUtils";
import {
  FieldHintContainer,
  FieldHintLabel,
  FieldHintText,
  FieldTitleText,
  FormControlLayout,
  InputField,
  ReferralButton,
  ReferralUpload,
} from "./AddPatientGroup.style";
import ReferralFilesPreview from "./ReferralFilesPreview";

type ReferralInfoFieldProps = {
  onReferralInfoChange: (key: string, value: string) => void;
  referralInfo: IReferralInfo;
  uploadFiles: any[];
  setUploadFiles: (files: any[]) => void;
  fileCaptionMap: Map<string, string>;
  onSetCaption: (fileName: string, caption: string) => void;
  onRemoveFile: (fileName: string) => void;
  referralSourceHintLabel?: string;
  referralSourceHint?: string;
  communityLiaisonHintLabel?: string;
  communityLiaisonHint?: string;
};

interface OptionsParam {
  inputValue?: string;
  label: string;
}

export const ReferralInfoField = React.forwardRef<
  HTMLDivElement,
  ReferralInfoFieldProps
>(
  (
    {
      onReferralInfoChange,
      referralInfo,
      uploadFiles,
      setUploadFiles,
      fileCaptionMap,
      onSetCaption,
      onRemoveFile,
      referralSourceHintLabel,
      referralSourceHint,
      communityLiaisonHintLabel,
      communityLiaisonHint,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const tNs = (key: string) => t(key, { ns: "patientGroup" });
    const [referralFiles, setReferralFiles] = useState<FileObjectType[]>(
      mapFilesToFileObjectType(referralInfo, uploadFiles),
    );

    useEffect(() => {
      setReferralFiles(mapFilesToFileObjectType(referralInfo, uploadFiles));
    }, [referralInfo]);

    useEffect(() => {
      const refExists =
        ref && typeof ref === "object" && "current" in ref && ref.current;
      if (uploadFiles.length > 0 && refExists && ref.current) {
        ref.current.scrollTop = ref.current.scrollHeight;
      }
    }, [uploadFiles]);

    const dropZoneErrorCallback = (errorMessage: string) => {
      enqueueSnackbar(errorMessage, {
        autoHideDuration: 3000,
      });
    };
    const { getRootProps, getInputProps } = useDropzone({
      ...getDropZoneBaseConfig({ errorCallback: dropZoneErrorCallback, t }),
      accept: ACCEPTED_FILE_TYPES_WITHOUT_AUDIO_VIDEO,
      noDrag: true,
      onDrop: useCallback(
        (acceptedFiles: File[]) => {
          setReferralFiles([
            ...referralFiles,
            ...acceptedFiles.map((acceptedFile: File) => {
              const fileLike: any = {
                ...acceptedFile,
                name: acceptedFile.name,
                size: acceptedFile.size,
                type: acceptedFile.type,
                filePath: "",
                uniqueName: acceptedFile.name,
                ext: "",
                isImage: () => acceptedFile.type.startsWith("image/"),
                isVideo: () => acceptedFile.type.startsWith("video/"),
                isAudio: () => acceptedFile.type.startsWith("audio/"),
                isDoc: () =>
                  !acceptedFile.type.startsWith("image/") &&
                  !acceptedFile.type.startsWith("video/") &&
                  !acceptedFile.type.startsWith("audio/"),
              };
              const fileParams: FileObjectType = getFileParams(fileLike);
              Object.assign(fileParams, {
                preview: URL.createObjectURL(acceptedFile),
              });
              return fileParams;
            }),
          ]);
          setUploadFiles([...uploadFiles, ...acceptedFiles]);
        },
        [referralFiles, uploadFiles],
      ),
    });

    const removeReferralFile = (fileParams: FileObjectType): void => {
      const newFiles = [...referralFiles];
      newFiles.splice(newFiles.indexOf(fileParams), 1);
      setReferralFiles(newFiles);
      onRemoveFile(fileParams.fileName ?? "");
    };

    const filter = createFilterOptions<OptionsParam>();

    // Shared hooks for referral source and community liaison API logic
    const referralSource = useReferralFieldAutocomplete(
      "referralSource",
      referralInfo.referralSource,
      onReferralInfoChange,
    );

    const communityLiaison = useReferralFieldAutocomplete(
      "communityLiaison",
      referralInfo.communityLiaison,
      onReferralInfoChange,
    );

    // MUI-specific: local open/close state for dropdowns
    const [openRS, setOpenRS] = React.useState<boolean>(false);
    const [referralSourceValue, setReferralSourceValue] =
      React.useState<OptionsParam>(
        referralInfo?.referralSource
          ? { label: referralInfo.referralSource }
          : { label: "" },
      );

    const [openCL, setOpenCL] = React.useState<boolean>(false);
    const [communityLiaisoningValue, setCommunityLiaisoningValue] =
      React.useState<OptionsParam>(
        referralInfo?.communityLiaison
          ? { label: referralInfo.communityLiaison }
          : { label: "" },
      );

    useEffect(() => {
      setReferralSourceValue(
        referralInfo.referralSource
          ? { label: referralInfo.referralSource }
          : { label: "" },
      );
      setCommunityLiaisoningValue(
        referralInfo.communityLiaison
          ? { label: referralInfo.communityLiaison }
          : { label: "" },
      );
    }, [referralInfo]);

    // Map shared hook options to MUI OptionsParam format
    const referralSourcesOptions: OptionsParam[] = referralSource.options.map(
      opt => ({ label: opt.label }),
    );
    const communityLiaisoningOptions: OptionsParam[] =
      communityLiaison.options.map(opt => ({ label: opt.label }));

    const handleAddOptionClickRS = async (
      event: React.MouseEvent<HTMLButtonElement> | null,
      newValue: OptionsParam,
    ) => {
      if (newValue.inputValue) {
        const added = await referralSource.onAddNew(newValue.inputValue);
        if (added) {
          setReferralSourceValue({ label: newValue.inputValue.toLowerCase() });
          setOpenRS(false);
        }
      }
    };

    const handleOptionChangeRS = (
      event: React.SyntheticEvent,
      value: string | OptionsParam,
      reason: string,
    ) => {
      if (typeof value === "string") {
        onReferralInfoChange("referralSource", value);
        setReferralSourceValue({ label: value });
      } else if (value && value.inputValue) {
        onReferralInfoChange("referralSource", value.inputValue);
        setReferralSourceValue({ label: value.inputValue });
      } else {
        onReferralInfoChange("referralSource", value?.label ?? "");
        setReferralSourceValue(value as OptionsParam);
        setOpenRS(false);
      }
    };

    const handleAddOptionClickCL = async (
      event: React.MouseEvent<HTMLButtonElement> | null,
      newValue: OptionsParam,
    ) => {
      if (newValue.inputValue) {
        const added = await communityLiaison.onAddNew(newValue.inputValue);
        if (added) {
          setCommunityLiaisoningValue({
            label: newValue.inputValue.toLowerCase(),
          });
          setOpenCL(false);
        }
      }
    };

    const handleOptionChangeCL = (
      event: React.SyntheticEvent,
      value: string | OptionsParam,
      reason: string,
    ) => {
      if (typeof value === "string") {
        onReferralInfoChange("communityLiaison", value);
        setCommunityLiaisoningValue({ label: value });
      } else if (value && value.inputValue) {
        onReferralInfoChange("communityLiaison", value.inputValue);
        setCommunityLiaisoningValue({ label: value.inputValue });
      } else {
        onReferralInfoChange("communityLiaison", value?.label ?? "");
        setCommunityLiaisoningValue(value as OptionsParam);
        setOpenCL(false);
      }
    };

    return (
      <FormControlLayout>
        <FieldTitleText>{tNs("referralInfoTitle")}</FieldTitleText>
        {referralSourceHint && (
          <FieldHintContainer>
            <FieldHintLabel>{tNs("referralSourceHintLabel")}:</FieldHintLabel>
            <FieldHintText>{referralSourceHint}</FieldHintText>
          </FieldHintContainer>
        )}
        <Autocomplete
          id="referral-source"
          data-testid="referral-source"
          open={openRS}
          onOpen={() => setOpenRS(true)}
          onBlur={() => {
            setOpenRS(false);
          }}
          value={referralSourceValue}
          disableClearable
          handleHomeEndKeys
          onChange={handleOptionChangeRS}
          options={referralSourcesOptions}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);

            const { inputValue } = params;
            if (
              filtered.length === 0 &&
              inputValue !== "" &&
              referralSource.showAddMessage
            ) {
              filtered.push({
                inputValue,
                label: tNs("tapToAddInList"),
              });
            }

            return filtered;
          }}
          getOptionLabel={option => {
            if (typeof option === "string") {
              return option;
            }
            if (option.inputValue) {
              return option.inputValue;
            }
            return option.label;
          }}
          renderOption={({ key, ...props }, option) => (
            <li
              key={key}
              {...props}
              onClick={event => {
                if (option.inputValue) {
                  handleAddOptionClickRS(null, option);
                } else {
                  handleOptionChangeRS(
                    event as React.SyntheticEvent,
                    option,
                    "selectOption",
                  );
                }
              }}
            >
              {option.label}
            </li>
          )}
          freeSolo
          renderInput={params => (
            <InputField
              {...params}
              label={tNs("referralSourceLabel")}
              type="text"
              variant="standard"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = e.target.value;
                onReferralInfoChange("referralSource", newValue);
                referralSource.onSearch(newValue).catch(() => {});
              }}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  type: "search",
                },
              }}
            />
          )}
        />
        {communityLiaisonHint && (
          <FieldHintContainer>
            <FieldHintLabel>{tNs("communityLiaisonHintLabel")}:</FieldHintLabel>
            <FieldHintText>{communityLiaisonHint}</FieldHintText>
          </FieldHintContainer>
        )}
        <Autocomplete
          id="community-liaison"
          data-testid="community-liaison"
          open={openCL}
          onOpen={() => setOpenCL(true)}
          onBlur={() => {
            setOpenCL(false);
          }}
          value={communityLiaisoningValue}
          disableClearable
          handleHomeEndKeys
          onChange={handleOptionChangeCL}
          options={communityLiaisoningOptions}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);

            const { inputValue } = params;
            if (
              filtered.length === 0 &&
              inputValue !== "" &&
              communityLiaison.showAddMessage
            ) {
              filtered.push({
                inputValue,
                label: tNs("tapToAddInList"),
              });
            }

            return filtered;
          }}
          getOptionLabel={option => {
            if (typeof option === "string") {
              return option;
            }
            if (option.inputValue) {
              return option.inputValue;
            }
            return option.label;
          }}
          renderOption={({ key, ...props }, option) => (
            <li
              key={key}
              {...props}
              onClick={event => {
                if (option.inputValue) {
                  handleAddOptionClickCL(null, option);
                } else {
                  handleOptionChangeCL(
                    event as React.SyntheticEvent,
                    option,
                    "selectOption",
                  );
                }
              }}
            >
              {option.label}
            </li>
          )}
          freeSolo
          renderInput={params => (
            <InputField
              {...params}
              label={tNs("communityLiaisonLabel")}
              type="text"
              variant="standard"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = e.target.value;
                onReferralInfoChange("communityLiaison", newValue);
                communityLiaison.onSearch(newValue).catch(() => {});
              }}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  type: "search",
                },
              }}
            />
          )}
        />
        <ReferralFilesWrapper>
          <ReferralUpload
            data-testid="referral-files-upload"
            {...getRootProps({ className: "dropzone" })}
          >
            <input {...getInputProps()} data-testid="files-input" />
            <ReferralButton
              data-testid="add-referral-button"
              variant="contained"
              fullWidth
              color="secondary"
              disableElevation
            >
              <AddIcon color="secondary" />
            </ReferralButton>
          </ReferralUpload>
          <ReferralFilesPreview
            files={referralFiles}
            fileCaptionMap={fileCaptionMap}
            onSetCaption={onSetCaption}
            removeReferralFile={removeReferralFile}
          />
        </ReferralFilesWrapper>
      </FormControlLayout>
    );
  },
);
